import React from "react";
import SignatureCanvas from "react-signature-canvas";
import { Table, Popup, Button, Image, CheckboxProps, Checkbox } from 'semantic-ui-react';
import gql from "graphql-tag";
import { apolloClientHolder } from "@crispico/foundation-react";
import { FindByStringParams } from "@crispico/foundation-react/entity_crud/FindByStringParams";

const html2pdf = require('html2pdf.js');

class Props {
  data: { [key: string]: string } = {};
  oilGroupName: string = "";
  paymentInfo?: { cardId: string, expirationDate: string };
  userData?: FuelingReportUserData;
}

export class FuelingReportUserData {
  signatures: { [key: string]: string } = {}
  checkboxes: { [key: string]: boolean } = {};
  remarks: string = ""
}

class FuelingReportState extends FuelingReportUserData {
  oilGroupHeaderImage?: string;
  oilGroupHeaderText?: string;
  headerText?: string;
  showSignaturePopup?: string;
  readOnly: boolean = false;
}

const COMMERCIAL = "commercial";
const PRIVATE = "private";
const WITH_TVA = "withTVA";
const WITHOUT_TVA = "withoutTVA";
const FREE_OF_WATER = "freeOfWater";
const DRIVER = 'representative';

const opposites: { [key: string]: string } = {
  [COMMERCIAL]: PRIVATE,
  [PRIVATE]: COMMERCIAL,
  [WITH_TVA]: WITHOUT_TVA,
  [WITHOUT_TVA]: WITH_TVA
};

export class FuelingReport extends React.Component<Props, FuelingReportState> {

  private sigCanvasRef = React.createRef<SignatureCanvas>();
  private printRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);
    this.state = this.initializeStateFromProps(props);
  }

  async componentDidMount() {
    await this.getHeader(this.props.oilGroupName);
  }

  async getHeader(oilGroupName: string) {
    const query = gql(`query getHeader($params: FindByStringParamsInput){
      oilGroupService_findByString(params: $params) {
        headerImagePath headerText
      }
      mobileDeviceService_fuelingSettings {
        pdfHeaderText
      }
    } `);

    const queryLogo = gql(`query getLogoBase64($url: String!) {
      oilGroupService_logoBase64(url: $url)
    }`);

    const result = (await apolloClientHolder.apolloClient.query({
      query,
      variables: FindByStringParams.create().string(oilGroupName),
      context: { showSpinner: false }
    }));

    var logo = null;
    if (result.data["oilGroupService_findByString"][0].headerImagePath) {
      logo = (await apolloClientHolder.apolloClient.query({
        query: queryLogo,
        variables: { url: result.data["oilGroupService_findByString"][0].headerImagePath },
        context: { showSpinner: false }
      }));
    }

    this.setState({
      oilGroupHeaderImage: logo?.data["oilGroupService_logoBase64"],
      oilGroupHeaderText: result.data["oilGroupService_findByString"][0].headerText?.replace(/\r\n/g, "\n").replace(/\r/g, "\n"),
      headerText: result.data["mobileDeviceService_fuelingSettings"].pdfHeaderText?.replace(/\r\n/g, "\n").replace(/\r/g, "\n")
    })
  }

  private initializeStateFromProps(props: Props): FuelingReportState {
    const userData = props.userData;

    return {
      signatures: userData?.signatures || {},
      checkboxes: userData ? (userData?.checkboxes || {}) : { [COMMERCIAL]: true, [WITHOUT_TVA]: true, [FREE_OF_WATER]: true },
      remarks: userData?.remarks || "",
      readOnly: userData ? true : false
    };
  }

  getPdf = async () => {
    const node = this.printRef.current;

    if (!node) {
      return undefined;
    }

    const base64 = await html2pdf()
      .set({
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }, html2canvas: { scale: 2, useCORS: true },
        margin: 2
      })
      .from(node)
      .outputPdf('datauristring');

    return base64.split(',')[1]; //Remove MIME type prefix, return only the base64-encoded PDF content, e.g. of content `data:application/pdf;base64,JVBERi0xLjQKJaqrrK0...`
  }

  getValues = async (): Promise<{ userData: FuelingReportUserData; pdf?: string; }> => {
    const { signatures, checkboxes, remarks } = this.state;

    if (!signatures[DRIVER]) {
      throw Error("Le PDF ne sera pas enregistré car il n'est pas signé");
    }

    const pdf = await this.getPdf();

    return { userData: { signatures, checkboxes, remarks }, pdf };
  }

  saveSignature = (name: string) => {
    const signatureDataUrl = this.sigCanvasRef?.current?.toDataURL();

    if (signatureDataUrl) {
      this.setState(prevState => ({
        signatures: { ...prevState.signatures, [name]: signatureDataUrl },
        showSignaturePopup: undefined
      }));
    }
  }

  getSignature = (name: string) => {
    const signatureDataUrl = this.state.signatures[name];

    return (
      <div style={{ placeSelf: "center" }}>
        <div style={{ width: 150, height: 75, cursor: this.state.readOnly ? undefined : 'pointer' }}>
          {signatureDataUrl ? (
            <Image style={{ width: "100%", height: "100%" }} src={signatureDataUrl} />
          ) : (
            this.state.readOnly ? undefined :
              <div style={{ width: "100%", height: "100%", border: '1px dashed #ccc', textAlign: 'center', alignContent: 'center' }}>
                <span style={{ color: '#999', fontSize: 12 }}>Cliquez pour signer</span>
              </div>
          )}
        </div>
      </div>
    );
  }

  renderSignaturePopup = (name: string) => {
    return <Popup
      on="click"
      open={this.state.showSignaturePopup === name}
      onClose={() => this.setState(prevState => ({ showSignaturePopup: undefined }))}
      onOpen={() => this.setState(prevState => ({ showSignaturePopup: name }))}
      trigger={this.getSignature(name)}
      position="top left"
      disabled={this.state.readOnly}
    >
      <div>
        <div style={{ border: '1px solid #000' }}>
          <SignatureCanvas
            ref={this.sigCanvasRef}
            penColor="black"
            canvasProps={{ width: 300, height: 150 }}
          />
        </div>
        <div style={{ marginTop: 10, textAlign: 'right' }}>
          <Button size="small" onClick={() => this.sigCanvasRef?.current?.clear()}>Supprimer</Button>
          <Button size="small" primary onClick={() => this.saveSignature(name)}>Sauvegarder</Button>
        </div>
      </div>
    </Popup>;
  }

  handleCheckboxChange = (_: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
    const { name, checked } = data;
    this.setState(prevState => {
      const newCheckboxState = { ...prevState.checkboxes };

      if (checked) {
        const oppositeName = opposites[name!];
        if (oppositeName) {
          newCheckboxState[oppositeName] = false;
        }
      }

      newCheckboxState[name!] = checked!;

      return {
        checkboxes: newCheckboxState
      }
    });
  };

  getCheckbox(label: string | JSX.Element, name: string) {
    return <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{label}</span>
      <Checkbox
        style={{ padding: 0, margin: 0, marginRight: "5px" }}
        name={name}
        checked={this.state.checkboxes[name]}
        onChange={this.handleCheckboxChange}
        readOnly={this.state.readOnly}
      />
    </span>;
  }

  getRemarksArea() {
    const value = this.state.readOnly && !this.state.remarks ? " " : this.state.remarks;
    return <div>
      <textarea
        placeholder="Cliquez pour commencer à éditer"
        style={{ border: value ? "none" : '1px dashed #ccc', backgroundColor: 'transparent', padding: '10px', width: '100%', height: '200px', resize: 'none', outline: 'none', overflow: 'hidden' }}
        value={value}
        onChange={(e) => this.setState(prevState => ({ remarks: e.target.value }))}
        readOnly={this.state.readOnly}
      />
    </div>
  }

  render() {
    return (
      <div ref={this.printRef} >
        <Table celled style={{ margin: 0, padding: 0, fontSize: 'small' }}>
          <Table.Body>
            <Table.Row>
              <Table.Cell style={{ margin: 0, padding: 0 }} colSpan="2" textAlign="left">
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <img style={{ height: '90px', objectFit: 'contain', marginLeft: "50px", marginRight: "50px", alignSelf: "center" }}
                    src={this.state.oilGroupHeaderImage}
                  />
                  <div>
                    {this.state.oilGroupHeaderText?.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell style={{ margin: 0, padding: 0, alignContent: "center", width: "30%" }} textAlign="center">
                {this.state.headerText?.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell style={{ margin: 0, padding: 0, backgroundColor: '#000', color: '#fff' }} textAlign="center">
                JET A1
              </Table.Cell>
              <Table.Cell style={{ margin: 0, padding: 0 }} colSpan="2" textAlign="center">
                BON DE LIVRAISON
              </Table.Cell>
            </Table.Row>

            <Table.Row verticalAlign="top">
              <Table.Cell width={7} style={{ margin: 0, padding: 0 }}>
                <p><u>Carte de crédit:</u></p>
                {this.props.paymentInfo ? "N° Bon de Livraison: " + this.props.paymentInfo?.cardId : " "}
                <br />
                {this.props.paymentInfo ? "Date d'expiration: " + this.props.paymentInfo?.expirationDate : " "}
                {new Array(13).fill(null).map((_, i) => <br key={i} />)}

                <Table celled style={{ margin: 0, padding: 0 }}>
                  <Table.Body>
                    <Table.Row style={{ margin: 0, padding: 0 }}>
                      <Table.Cell style={{ margin: 0, padding: 0 }} width={6}> </Table.Cell>
                      <Table.Cell style={{ margin: 0, padding: 0 }} width={3} textAlign="center">Px au Litre</Table.Cell>
                      <Table.Cell style={{ margin: 0, padding: 0 }} width={2} textAlign="center">Montant</Table.Cell>
                    </Table.Row>
                    {["Prix / Price", "Taxe carbone / Carbon Tax", "TIC / MOT", "Sous Total HT", "TVA / Valeur Added Tax", "Total TTC"].map((label, i) => (
                      <Table.Row key={i}>
                        <Table.Cell style={{ margin: 0, padding: 0 }}>{label}</Table.Cell>
                        <Table.Cell style={{ margin: 0, padding: 0 }}></Table.Cell>
                        <Table.Cell style={{ margin: 0, padding: 0 }}></Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>

                <u>Remarques / Remarks:</u>
                {this.getRemarksArea()}
              </Table.Cell>

              <Table.Cell colSpan="2" style={{ margin: 0, padding: 0 }}>
                <Table celled style={{ borderTop: 0, borderLeft: 0, borderRight: 0, margin: 0 }}>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell width={6} style={{ margin: 0, padding: 0 }}>{this.getCheckbox("Commercial", COMMERCIAL)}</Table.Cell>
                      <Table.Cell style={{ margin: 0, padding: 0 }}>{this.getCheckbox("Privé", PRIVATE)}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell style={{ margin: 0, padding: 0 }}>{this.getCheckbox("Sans TVA", WITHOUT_TVA)}</Table.Cell>
                      <Table.Cell style={{ margin: 0, padding: 0 }}>{this.getCheckbox("Avec TVA", WITH_TVA)}</Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
                <Table celled style={{ margin: 0, padding: 0 }}>
                  <Table.Body>
                    {[
                      ["N° Bon de Livraison", "N° Ticket", "nrTicket"],
                      ["Date", "Date", "date"],
                      ["Client", "Customer", "airline"],
                      ["N° Vol", "Flight Number", "flight"],
                      ["Immatriculation", "Registration", "registration"],
                      ["Type Aéronef", "Aircraft Type", "aircraftType"],
                      ["Destination", "Destination", "destination"],
                      ["Parking", "Position", "parking"],
                      ["N° Véhicule", "Vehicle Number", "vehicleNr"],
                      ["N° Hydrant", "Pit Number", "pitNr"],
                      ["En place", "At Position", "atPosition"],
                      ["Début opération", "Start Operation", "startOperation"],
                      ["Fin Opération", "Stop Operation", "stopOperation"],
                      ["Index Début", "Meter Start", "startIndex"],
                      ["Index Fin", "Meter Stop", "endIndex"],
                      ["Quantité", "Quantity", "quantity"]
                    ].map(([labelUp, labelDown, index], i) => (
                      <Table.Row key={i}>
                        <Table.Cell width={6} style={{ margin: 0, padding: 0 }}>
                          {labelUp}<br />
                          <i style={{ fontSize: 'smaller' }}>{labelDown}</i>
                        </Table.Cell>
                        <Table.Cell style={{ margin: 0, padding: 0, alignContent: "center" }}>{this.props.data?.[index]}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell colSpan="3" style={{ margin: 0, padding: 0 }}>
                <p style={{ fontSize: "xx-small", margin: 0 }}>
                  Seuls les volumes indiqués par les compteurs sont garantis - ATTENTION : CARBURANT AVIATION A FISCALITÉ SPÉCIFIQUE ET AUX USAGES RÉGLEMENTÉS
                  (arrêté ministériel du 22/12/1978 et décret du 26 juin 2009). INTERDIT A TOUS AUTRES USAGES NON SPÉCIALEMENT AUTORISÉS.
                  -Carburéacteur de type pétrole lampant-
                </p>
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell style={{ margin: 0, padding: 0 }}>
                {this.getCheckbox(
                  <p style={{ fontSize: 'xx-small', margin: 0 }}>
                    Le produit est exempt d’eau et d’impureté <br />delivered product is free of water and dirt.
                  </p>, FREE_OF_WATER)}
                <div style={{ borderTop: '1px solid #ccc' }} />
                <span>NOM ET SIGNATURE Représentant {this.props.oilGroupName}</span>
                {this.renderSignaturePopup(DRIVER)}
              </Table.Cell>

              <Table.Cell textAlign="center" style={{ margin: 0, padding: 0 }}>
                <div>NOM ET SIGNATURE DU CLIENT</div>
                <br />
                <div>
                  {this.renderSignaturePopup('client')}
                </div>
              </Table.Cell>

              <Table.Cell textAlign="center" style={{ margin: 0, padding: 0 }}>
                VISA DOUANE ou Aéroport
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    );
  }

}
